/**
 * Registry glass (metrics substrate S5) — a read-only
 * window onto the atom ledger. Every Atom registers before
 * anything captures or displays it; "not in the registry = not tracked."
 *
 * The canon (atoms; cards were wiped by #740) ships in the bundle (@command-center/shared →
 * registry.json), so the tables render with no fetch — this IS the canon, not
 * a copy of it. The one live piece is the ledger status strip: GET
 * /api/registry, the per-atom-type landed counts + newest event_date, the
 * per-atom-type revisions (count + newest revision day) and latest state
 * (#1180), the latest extraction run marker, the ledger's ship day and the
 * revisions' provenance id (the Contract's entries, read by the worker — the
 * Contract's prose never ships in this bundle). A glass you look into; writes go through
 * Claude editing registry.json.
 */

import { ATOMS, type Atom } from "@command-center/shared";
import { useRegistryStatus } from "@/lib/use-registry";

const CANON_PATH = "packages/shared/src/registry/registry.json";

export function RegistryPage() {
  return (
    <section className="mx-auto max-w-6xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">Registry</h1>
      <p className="mb-1 text-sm text-plane-muted">
        The atom ledger — every atom registers here before it
        is captured or displayed. Not in the registry = not tracked.
      </p>
      <p className="mb-5 text-xs text-plane-muted">
        Canon lives in <code className="text-plane-muted">{CANON_PATH}</code>{" "}
        (edited through Claude); this page renders it read-only.
      </p>

      <LedgerStatus />
      <AtomsTable />
    </section>
  );
}

/** The one live piece — the ledger's landed footprint. */
function LedgerStatus() {
  const query = useRegistryStatus();
  const run = query.data?.latestRun ?? null;
  const total =
    query.data?.atoms.reduce((sum, a) => sum + a.count, 0) ?? null;
  const revisions =
    query.data?.atoms.reduce((sum, a) => sum + a.revisionCount, 0) ?? 0;
  const latestState =
    query.data?.atoms.reduce((sum, a) => sum + a.latestStateCount, 0) ?? 0;
  const newestRevision =
    query.data?.atoms.reduce<string | null>(
      (max, a) =>
        a.latestRevisionDay !== null && (max === null || a.latestRevisionDay > max)
          ? a.latestRevisionDay
          : max,
      null,
    ) ?? null;
  const revisionsShip = query.data?.revisionsShip ?? null;

  return (
    <div className="mb-6 rounded-md border border-border bg-muted p-4">
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        Ledger
      </div>
      {query.isLoading ? (
        <div className="text-sm text-plane-muted" data-testid="registry-ledger-loading">
          Loading…
        </div>
      ) : query.isError ? (
        <div className="text-sm text-error" data-testid="registry-ledger-error">
          Couldn't load the ledger status. It will retry automatically.
        </div>
      ) : (
        <div className="text-sm" data-testid="registry-ledger">
          {total !== null && total > 0 ? (
            <>
              <span className="font-medium">{total.toLocaleString()}</span> atoms
              landed
              {run !== null ? (
                <>
                  {" "}· last run {run.dayAnchor} (+{run.atomsInserted})
                </>
              ) : null}
              <div data-testid="registry-ledger-revisions">
                <span className="font-medium">{revisions.toLocaleString()}</span>{" "}
                revisions
                {newestRevision !== null ? <> · newest {newestRevision}</> : null}
                {" "}· latest state{" "}
                <span className="font-medium">{latestState.toLocaleString()}</span>
              </div>
            </>
          ) : (
            <span className="text-plane-muted">
              No atoms landed yet — recording begins on the ledger's ship day
              {query.data?.ledgerShip ? ` (${query.data.ledgerShip.on}; engineering/contract/provenance.ts ${query.data.ledgerShip.id})` : ""}.
            </span>
          )}
          {/* The ledger's story beside its number, by pointer (the glass rule,
              #890): provenance.ts atom_ledger_ship holds it. */}
          <div className="mt-1 text-xs text-plane-muted" data-testid="registry-ledger-story">
            The ledger records each row once, on the day it was first seen — a row the
            base later deleted, un-stamped or re-dated stays as first seen; the base is
            the live count, the ledger the history of first sightings. A run's marker
            counts what landed for its own day; an atom landing late for an
            already-marked day is inserted and never tallied — the rows are the count
            {query.data?.ledgerShip ? ` (engineering/contract/provenance.ts ${query.data.ledgerShip.id})` : ""}.
          </div>
          {/* What the two numbers count, and the revisions' story by pointer:
              provenance.ts atom_revisions_ship holds it. */}
          <div className="mt-1 text-xs text-plane-muted" data-testid="registry-revisions-story">
            Revisions are the metric_atom_revisions rows; the latest state counts the atoms
            whose newest revision, else first sighting, has a value
            {revisionsShip !== null ? ` (engineering/contract/provenance.ts ${revisionsShip.id})` : ""}.
          </div>
        </div>
      )}
    </div>
  );
}

function AtomsTable() {
  const query = useRegistryStatus();
  const statusByType = new Map(
    (query.data?.atoms ?? []).map((a) => [a.atomType, a]),
  );
  // The ledger's first day — the Contract's provenance entry, carried by the
  // route so every gated atom's count names its rule (the glass rule, #890);
  // and an atom's own first day where registry.json names one, resolved by the
  // route the same way.
  const ship = query.data?.ledgerShip ?? null;
  const boundaryByType = new Map(
    (query.data?.atomBoundaries ?? []).map((b) => [b.atomType, b]),
  );

  return (
    <>
      <h2 className="mb-2 mt-2 text-sm font-semibold">Atoms ({ATOMS.length})</h2>
      <p className="mb-3 text-xs text-plane-muted">
        Daily facts extracted from Airtable stamps — What / How / When, no Why.
      </p>
      <div className="mb-8 overflow-x-auto">
        <table className="w-full border-collapse text-left text-xs">
          <thead>
            <tr className="border-b border-border text-plane-muted">
              <Th>Atom</Th>
              <Th>What</Th>
              <Th>How</Th>
              <Th>When</Th>
              <Th>Keys</Th>
              <Th>Landed</Th>
            </tr>
          </thead>
          <tbody>
            {ATOMS.map((atom) => (
              <AtomRow
                key={atom.id}
                atom={atom}
                landed={statusByType.get(atom.id) ?? null}
                ship={ship}
                boundary={boundaryByType.get(atom.id) ?? null}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AtomRow({
  atom,
  landed,
  ship,
  boundary,
}: {
  atom: Atom;
  landed: {
    count: number;
    latestEventDate: string | null;
    revisionCount: number;
    latestRevisionDay: string | null;
    latestStateCount: number;
  } | null;
  ship: { id: string; on: string } | null;
  boundary: { id: string; on: string } | null;
}) {
  return (
    <tr className="border-b border-border/60 align-top">
      <Td>
        <code className="whitespace-nowrap">{atom.id}</code>
        <div className="mt-0.5 text-plane-muted">{atom.type}</div>
      </Td>
      <Td>{atom.what}</Td>
      <Td>
        <div className="text-plane-muted">
          {atom.how.sourceFields.map((f) => f.name).join(", ")}
        </div>
        <div>{atom.how.predicate}</div>
      </Td>
      <Td>
        <div>{atom.when.cadence}</div>
        <div
          className="text-plane-muted"
          title={
            boundary !== null
              ? `this atom's own first day — engineering/contract/provenance.ts ${boundary.id}`
              : ship !== null
                ? `the ledger's ship day — engineering/contract/provenance.ts ${ship.id}`
                : "the ledger's first day (loading)"
          }
        >
          recording since {boundary?.on ?? ship?.on ?? "…"}
        </div>
      </Td>
      <Td>
        <span className="text-plane-muted">{atom.keys.join(", ") || "—"}</span>
      </Td>
      <Td>
        {landed !== null ? (
          <>
            <span className="font-medium">{landed.count.toLocaleString()}</span>
            {landed.latestEventDate !== null ? (
              <div className="text-plane-muted">{landed.latestEventDate}</div>
            ) : null}
            <div className="text-plane-muted" data-testid={`registry-atom-revisions-${atom.id}`}>
              {landed.revisionCount.toLocaleString()} rev
              {landed.latestRevisionDay !== null ? ` · ${landed.latestRevisionDay}` : ""}
              {" "}· now {landed.latestStateCount.toLocaleString()}
            </div>
          </>
        ) : (
          <span className="text-plane-muted">0</span>
        )}
      </Td>
    </tr>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-2 py-1.5 font-medium">{children}</th>;
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-2 py-2">{children}</td>;
}
